import { useCallback, useContext, useReducer, useState } from 'react';
import { AnyObjectSchema } from 'yup';

import { useDialog } from '../../dialog/useDialog';
import errors from '../../../../constants/errors';
import { ToastNotificationContext } from '../../toast-notification';
import { useHandleApiError } from '../../../../api/useHandleApiError';
import { getGenerateReportBusinessError } from './businessErrors';
import { handleResponseError } from '../../../../api/handleResponseError';

import { HandleGenerateReport, HandleGenerateReportRequest, Report } from '../interfaces';
import { useGenerateReportFromPredefinedData } from './useGenerateReportFromPredefinedData';
import { generateReportReducer, initialState, State, STEP, Action } from './generateReportReducer';

interface Props {
  handleSuccess: (report: Report) => void;
  handleClose: () => void;
  handleGenerateReportRequest: HandleGenerateReportRequest<unknown>;
  pendingLimitReached: boolean;
  loading: boolean;
  predefinedReportGeneration: boolean;
  filtersValidationSchema: AnyObjectSchema;
}
export const useGenerateReport = ({
  handleSuccess,
  handleClose,
  handleGenerateReportRequest,
  pendingLimitReached,
  loading,
  predefinedReportGeneration,
  filtersValidationSchema
}: Props) => {
  const { openToast } = useContext(ToastNotificationContext);
  const handleApiError = useHandleApiError();
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [generateReportState, dispatch] = useReducer(
    (prevState: State, action: Action) => generateReportReducer(prevState, action, filtersValidationSchema),
    initialState
  );
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const handleOpenNextPage = () => {
    dispatch({ type: STEP.FILTERS });
  };

  const handleGenerateReport: HandleGenerateReport<unknown> = async values => {
    try {
      setIsGenerating(true);
      const response = await handleGenerateReportRequest(values);

      if (response.status !== 200) {
        await handleResponseError(response);
      }

      handleSuccess(await response.json());
      openToast({
        message: 'Report successfully accepted for generation',
        severity: 'success'
      });
      handleCloseDialog();
    } catch (message) {
      handleApiError({
        message,
        errorMapper: getGenerateReportBusinessError,
        handleBusinessError: () => {
          dispatch({ type: STEP.BUSINESS_ERROR, payload: { error: getGenerateReportBusinessError(message) } });
        }
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateReportModalOpen = useCallback(() => {
    dispatch({ type: STEP.NAME });
    handleOpenDialog();
  }, [handleOpenDialog]);

  const handleGeneratePredefinedDataModalOpen = useCallback(() => {
    if (pendingLimitReached) {
      dispatch({ type: STEP.BUSINESS_ERROR, payload: { error: errors.pendingReportsLimitReached } });
    } else {
      dispatch({ type: STEP.NAME });
    }
    handleOpenDialog();
  }, [pendingLimitReached, handleOpenDialog]);

  useGenerateReportFromPredefinedData({
    handleGeneratePredefinedDataModalOpen,
    predefinedReportGeneration,
    loading
  });

  const handleGenerateReportModalClose = () => {
    handleCloseDialog();
    handleClose();
  };

  return {
    generateReportModalOpen: isDialogOpen,
    handleGenerateReportModalOpen,
    handleGenerateReportModalClose,
    handleGenerateReport,
    handleOpenNextPage,
    generateReportState,
    isGenerating
  };
};
